import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';

interface TransactionBreakdownProps {
  jobId: string;
  isExpanded: boolean;
  onToggle: () => void;
}

export function TransactionBreakdown({ jobId, isExpanded, onToggle }: TransactionBreakdownProps) {
  const { data: transactions, isLoading } = useQuery({
    queryKey: ['escrow-transactions', jobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_transactions')
        .select('*')
        .eq('job_id', jobId)
        .eq('status', 'held_in_escrow')
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data;
    },
  });

  const { data: reconciliation } = useQuery({
    queryKey: ['job-reconciliation', jobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_financial_reconciliations')
        .select('*')
        .eq('job_id', jobId)
        .eq('reconciliation_status', 'completed')
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading transaction details...</div>;
  }

  const totalVendorPayout = transactions?.reduce((sum, tx) => sum + (tx.vendor_payout_cents || 0), 0) || 0;
  const totalAmount = transactions?.reduce((sum, tx) => sum + (tx.total_amount_cents || 0), 0) || 0;
  const totalPlatformFee = transactions?.reduce((sum, tx) => sum + (tx.platform_fee_cents || 0), 0) || 0;
  const totalTax = transactions?.reduce((sum, tx) => sum + (tx.vendor_tax_cents || 0), 0) || 0;
  const totalCcFee = transactions?.reduce((sum, tx) => sum + (tx.cc_processing_fee_cents || 0), 0) || 0;
  const totalClientCharge = totalAmount + totalTax + totalCcFee;
  const totalVendorEarned = totalAmount - totalPlatformFee;

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-between">
          <span className="text-sm font-medium">View Transaction Breakdown</span>
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 pt-3">
        {reconciliation && (
          <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="secondary">Reconciliation Applied</Badge>
              {reconciliation.adjustment_count > 0 && (
                <span className="text-sm text-muted-foreground">
                  {reconciliation.adjustment_count} adjustment{reconciliation.adjustment_count > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Original PO:</span>
                <span className="line-through">${(reconciliation.original_po_amount_cents / 100).toFixed(2)}</span>
              </div>
              {reconciliation.total_credits_cents > 0 && (
                <div className="flex justify-between text-red-600 dark:text-red-400">
                  <span>Credits:</span>
                  <span>-${(reconciliation.total_credits_cents / 100).toFixed(2)}</span>
                </div>
              )}
              {reconciliation.total_increases_cents > 0 && (
                <div className="flex justify-between text-green-600 dark:text-green-400">
                  <span>Increases:</span>
                  <span>+${(reconciliation.total_increases_cents / 100).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-medium border-t pt-2">
                <span>Final PO:</span>
                <span>${(reconciliation.final_po_amount_cents / 100).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-medium text-green-600 dark:text-green-400">
                <span>Final Vendor Payout:</span>
                <span>${(reconciliation.final_vendor_payout_cents / 100).toFixed(2)}</span>
              </div>
              {reconciliation.pending_refund_cents > 0 && (
                <div className="flex justify-between text-sm text-amber-600 dark:text-amber-400">
                  <span>Pending Refund:</span>
                  <span>${(reconciliation.pending_refund_cents / 100).toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="border rounded-lg divide-y">
          {transactions?.map((tx, index) => {
            const isAdjustment = !!tx.adjustment_id;
            const txType = isAdjustment ? 'adjustment' : 'initial_payment';
            const isPrimary = !isAdjustment;
            
            return (
              <div key={tx.id} className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant={isPrimary ? 'default' : 'secondary'}>
                      {txType === 'initial_payment' ? 'Initial Payment' : 'Adjustment'}
                    </Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(tx.created_at), 'MMM d, yyyy')}
                  </span>
                </div>
                
                <div className="grid grid-cols-7 gap-2 text-xs">
                  <div>
                    <p className="text-muted-foreground">Client Charge</p>
                    <p className="font-medium">
                      ${(((tx.total_amount_cents || 0) + (tx.vendor_tax_cents || 0) + (tx.cc_processing_fee_cents || 0)) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tax Collected</p>
                    <p className="font-medium">
                      ${((tx.vendor_tax_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">CC Fee</p>
                    <p className="font-medium">
                      ${((tx.cc_processing_fee_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Base Amount</p>
                    <p className="font-medium">
                      ${((tx.total_amount_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Platform Fee</p>
                    <p className="font-medium">
                      ${((tx.platform_fee_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Vendor Earned</p>
                    <p className="font-medium">
                      ${(((tx.total_amount_cents || 0) - (tx.platform_fee_cents || 0)) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Vendor Payout</p>
                    <p className="font-semibold text-primary">
                      ${((tx.vendor_payout_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-muted/50 p-3 rounded-lg">
          <div className="grid grid-cols-7 gap-2 text-xs">
            <div>
              <p className="text-muted-foreground font-medium">Total Charged</p>
              <p className="font-semibold">
                ${(totalClientCharge / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground font-medium">Total Tax</p>
              <p className="font-semibold">
                ${(totalTax / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground font-medium">Total CC Fee</p>
              <p className="font-semibold">
                ${(totalCcFee / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground font-medium">Total Base</p>
              <p className="font-semibold">
                ${((reconciliation?.final_po_amount_cents || totalAmount) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground font-medium">Total Platform Fee</p>
              <p className="font-semibold">
                ${(totalPlatformFee / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground font-medium">Total Earned</p>
              <p className="font-semibold">
                ${(totalVendorEarned / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground font-medium">{reconciliation ? 'Final' : 'Total'} Payout</p>
              <p className="font-bold text-primary">
                ${((reconciliation?.final_vendor_payout_cents || totalVendorPayout) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
